import { AfterViewChecked, AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';

import { TranslateModule } from '@ngx-translate/core';
import { AssetsData } from '@payments/common-utils';
import { CardSubmitRequest, CardSubmitUseCase, EventType } from '@payments/ngpgcard-utils';
import { Subscription } from 'rxjs';

import { AppConstants, Config, Jurisdiction, ProductType } from '../../config/appConstants';
import { HeaderComponent } from '../../core/header/header.component';
import { DepositInfo } from '../../model/depositInfo.model';
import { QDInstrument } from '../../model/qdInstrument.model';
import { ApplePayDirectService } from '../../service/apple-pay-direct.service';
import { ApplePayValidatorService } from '../../service/apple-pay-validator.service';
import { BehaviorDataService } from '../../service/behaviorData.service';
import { CardIframeService } from '../../service/card-iframe-service';
import { CcDepositSubmitService } from '../../service/ccDepositSubmit.service';
import { DataService } from '../../service/data.service';
import { DepositService } from '../../service/deposit.service';
import { GeocomplyService } from '../../service/geocomply.service';
import { GtmService } from '../../service/gtm.service';
import { LoaderService } from '../../service/loader.service';
import { OptionListService } from '../../service/optionList.service';
import { ResizeService } from '../../service/resize.service';
import { StakeAmountService } from '../../service/stakeAmount.service';
import { UserDataService } from '../../service/userDataService';
import { WorldPayFetchSessionIdService } from '../../service/worldPayFetchSessionId.service';
import { DepositDeclinedComponent } from '../../shared/components/deposit-declined/deposit-declined.component';
import { ErrorCode, ErrorTemplate } from '../../shared/error.constants';
import { AdditionalMessagesComponent } from './additional-messages/additional-messages.component';
import { BottomMessageComponent } from './additional-messages/bottom-messages/bottom-messages.component';
import { DepositFailureComponent } from './deposit-failure/deposit-failure.component';
import { DepositProcessComponent } from './deposit-process/deposit-process.component';
import { DepositSuccessComponent } from './deposit-success/deposit-success.component';
import { OptionsListComponent } from './options-list/options-list.component';

@Component({
    selector: 'app-deposit',
    templateUrl: './deposit.component.html',
    imports: [
        HeaderComponent,
        DepositFailureComponent,
        AdditionalMessagesComponent,
        FormsModule,
        ReactiveFormsModule,
        OptionsListComponent,
        BottomMessageComponent,
        DepositProcessComponent,
        DepositSuccessComponent,
        TranslateModule,
        DepositDeclinedComponent,
    ],
})
export class DepositComponent implements AfterViewInit, OnInit, AfterViewChecked, OnDestroy {
    depositService = inject(DepositService);
    loaderService = inject(LoaderService);
    private userDataService = inject(UserDataService);
    dataService = inject(DataService);
    private ccDepositService = inject(CcDepositSubmitService);
    private behaviorData = inject(BehaviorDataService);
    optionListService = inject(OptionListService);
    private changeDetector = inject(ChangeDetectorRef);
    private stakeAmountService = inject(StakeAmountService);
    private wpSessionService = inject(WorldPayFetchSessionIdService);
    private gtmService = inject(GtmService);
    private geoComply = inject(GeocomplyService);
    private applePayService = inject(ApplePayValidatorService);
    private applePayDirectService = inject(ApplePayDirectService);
    private formBuilder = inject(FormBuilder);
    private cardIframeService = inject(CardIframeService);
    private resizeService = inject(ResizeService);
    private assetsData = inject(AssetsData);

    depositForm: FormGroup<{
        instrumentFormGroup: FormGroup;
        ccInputFormGroup: FormGroup;
    }>;

    kycPLverification: boolean;
    inputType: string;
    ccFamily = AppConstants.CCFAMILY;
    depositInfo: DepositInfo;
    selectedOption: QDInstrument;
    ccDepositCurrencyCode: string;
    productOptionDisabled: boolean;
    productList: any[];
    isBrandSupportBothCasinoAndPokerProduct: boolean;
    worldPaySessionIdStatus: boolean;
    stakeAmountToBePopulated: number;
    productPreference: string;
    prefillInDecimal: boolean;
    private subscriptions$ = new Subscription();
    depositCurfewCtaDisabled: boolean;
    isPaypalBadFunding: boolean;
    configParams: any;
    isSubmitClicked: boolean = false;

    ngOnInit() {
        this.resetDepositData();
        this.buildForm();
        this.depositService.showDepositInput.set(true);
        if (!this.optionListService?.dataLoading()) {
            this.initialize();
            this.getBehaviorData();
        }
    }

    ngAfterViewInit(): void {
        this.resizeService.resizeCashier();
    }

    initialize() {
        this.setDepositOptionInfo();
        this.loadConversionMap();
        this.handleSubmitResponse();
    }

    updatePaymentMethod(payName: string) {
        this.inputType = payName;
    }

    setDepositOptionInfo() {
        this.selectedOption = this.optionListService?.selectedOption;
        if (this.userDataService?.userData?.configParams) {
            const configParams = this.userDataService?.userData?.configParams;
            this.configParams = configParams;
            this.isBrandSupportBothCasinoAndPokerProduct = configParams?.isBrandSupportBothCasinoAndPokerProduct === Config.YES;
            this.depositService.depositInfo.germanAMLChangesEnabled = configParams?.germanAMLChangesEnabled === Config.YES;
            this.depositService.depositInfo.isBelgiumRgDecreeEnabled = configParams?.belgium_rg_decree_enabled === Config.YES;
            if (this.depositService?.depositInfo?.isBelgiumRgDecreeEnabled) {
                this.depositService.depositInfo.increaseLimitUrl = configParams?.deposit_Limits_Url;
            }
        }

        this.productList = this.userDataService?.userData?.productList;

        const product =
            this.dataService?.sessionInfo?.productId === ProductType.POKER && this.isBrandSupportBothCasinoAndPokerProduct
                ? ProductType.CASINO
                : this.dataService?.sessionInfo?.productId;
        this.behaviorData.changeProductPreference(
            this.productOptionDisabled && this.productList && this.productList.includes(ProductType.SPORTS) ? ProductType.SPORTSBOOK : product,
        );

        const threeds2Enabled = this.selectedOption?.threeds2Enabled;

        if (threeds2Enabled && this.userDataService?.userData?.configParams?.worldPayTokenForDDC) this.getSessionIdForWP();
    }

    loadConversionMap() {
        if (this.dataService?.sessionInfo?.stake && !this.stakeAmountService?.isStakeAmountServiceInitialized) {
            this.stakeAmountService.stake = Math.round(this.dataService?.sessionInfo?.stake * 100) / 100 / 100;
            this.stakeAmountService.retrieveAmountRequiredToMeetStake(this.prefillAmountRequiredToMeetStake);
        } else {
            this.prefillAmountRequiredToMeetStake();
        }
    }

    getSessionIdForWP() {
        const worldpayTimerDuration = this.userDataService?.userData?.configParams?.worldpayTimerDuration
            ? this.userDataService?.userData?.configParams?.worldpayTimerDuration * 1000
            : 5000;
        this.wpSessionService.fetchWPSessionId(
            this.userDataService?.userData?.configParams?.baseWorldPayURLForDDC,
            this.userDataService?.userData?.configParams?.worldPayTokenForDDC,
            this.userDataService?.userData?.configParams?.worldPayURLForDDC,
            this.selectedOption?.startDigits,
        );

        setTimeout(() => {
            if (!this.worldPaySessionIdStatus) {
                this.setWorldPaySessionId('');
            }
        }, worldpayTimerDuration);
    }

    setWorldPaySessionId(worldPaySessionId: any) {
        this.depositService.depositInfo.worldPaySessionId = worldPaySessionId;
        this.worldPaySessionIdStatus = true;
    }

    prefillAmountRequiredToMeetStake() {
        const amountToPopulate = this.stakeAmountService.getAmountToBePopulated(
            null,
            this.depositService?.conversionRates,
            this.optionListService?.selectedOption,
        );
        this.prefillNavAmount(parseFloat(amountToPopulate).toFixed(2));
    }

    prefillNavAmount(amountToBePrefilled: any) {
        this.selectedOption = this.optionListService?.selectedOption;
        if (this.selectedOption?.paymentMethodInfo?.paymentMethod.toUpperCase() !== AppConstants.FLASH1VOUCHER) {
            const minLimit = this.selectedOption?.paymentMethodInfo?.minDepositLimit?.value;
            const maxLimit = this.selectedOption?.paymentMethodInfo?.maxDepositLimit?.value;
            if (amountToBePrefilled !== '' && !isNaN(amountToBePrefilled)) {
                if (parseFloat(amountToBePrefilled) < minLimit) {
                    amountToBePrefilled = minLimit;
                }

                if (parseFloat(amountToBePrefilled) > maxLimit) {
                    amountToBePrefilled = maxLimit;
                }
                const amount = this.prefillInDecimal ? parseFloat(amountToBePrefilled).toFixed(2) : amountToBePrefilled;

                this.depositService.depositInfo.amountInAccountCurrency = amount;

                this.behaviorData.changeEnteredAmount(this.depositService?.depositInfo?.amountInAccountCurrency);
            }
        }

        this.depositService.setAmountCharged();
        this.depositService.getAmountInTransactionCurrency();
    }

    buildForm() {
        this.depositForm = this.formBuilder.group({
            instrumentFormGroup: new FormGroup({}),
            ccInputFormGroup: new FormGroup({}),
        });
    }
    applePayDepositSubmit() {
        const applePayData = {
            paymentMethod: this.depositService.depositInfo.paymentMethod?.toUpperCase(),
            amountInfo: {
                nativeCurrency: this.depositService?.depositInfo?.amountInfo?.nativeCurrency,
                nativeAmountString: this.depositService?.depositInfo?.amountInfo?.nativeAmountString,
            },
            bonusInfo: {
                bonusId: null,
                tncFromBonusService: null,
                bonusTnCAccepted: null,
                bonusCode: '',
            },
            browserInfo: {
                browserLanguage: this.depositService.depositInfo?.browserInfo?.browserLanguage,
                browserScreenHeight: this.depositService.depositInfo?.browserInfo?.browserScreenHeight,
                browserScreenWidth: this.depositService.depositInfo?.browserInfo?.browserScreenWidth,
                browserTimeZone: this.depositService.depositInfo?.browserInfo?.browserTimeZone,
            },
            germanAMLChangesEnabled: false,
            paymentAccountInfo: {
                paymethodID: '',
                paymentAccountId: '',
                cardType: '',
                originalCardEndDigits: '',
            },
            productPreference: 'SPORTSBOOK',
            worldPaySessionIdStatus: false,
            pageName: this.depositService.depositInfo.paymentMethod?.toUpperCase() + '_input',
        };
        let creditCardBanEnabled = 'N';
        const country = this.userDataService?.userData?.userInfo?.addressInfo?.country;
        if (
            this.depositService.depositInfo.paymentMethod?.toUpperCase() == 'APPLEPAYDIRECT' ||
            this.depositService.depositInfo.paymentMethod?.toUpperCase() == 'APPLEPAY'
        ) {
            creditCardBanEnabled =
                this.optionListService?.selectedOption?.paymentAccountInfo?.isESIntensivePlayer == 'Y' ||
                this.userDataService?.userData?.configParams?.creditCardBanEnabled == 'Y'
                    ? 'Y'
                    : 'N';
        }
        if (this.depositService.depositInfo.paymentMethod?.toUpperCase() == 'APPLEPAYDIRECT') {
            this.applePayDirectService.processApplePayDeposit(
                applePayData,
                creditCardBanEnabled,
                country,
                this.optionListService?.selectedOption?.paymentAccountInfo?.paymentAccountUpdateUrl,
                this.optionListService?.selectedOption?.paymentAccountInfo?.cardToken,
            );
        } else {
            this.applePayService.processApplePayDeposit(applePayData, creditCardBanEnabled, country);
        }
    }
    submitDeposit(): any {
        const blockMultipleSubmit = !!this.assetsData?.assets['miscimages']?.blockMultipleSubmit;
        const blockMultipleSubmitTimeout = Number.parseInt(this.assetsData?.assets['miscimages']?.blockMultipleSubmitTimeout || '0');
        if (blockMultipleSubmit) {
            this.isSubmitClicked = true;
            setTimeout(() => {
                this.isSubmitClicked = false;
            }, blockMultipleSubmitTimeout);
        }
        this.depositService.showDepositFailure.set(false);
        this.resizeService.resizeCashier();
        let proceedToSubmit = true;
        const threeds2Enabled = this.optionListService?.selectedOption?.threeds2Enabled;
        if (this.userDataService?.userData?.configParams?.worldPayTokenForDDC) {
            proceedToSubmit = false;
        }

        const hideProductRadioButtons = this.behaviorData?.getHideProductRadioButtons.subscribe((data) => {
            return data;
        });

        if (
            this.depositService.depositInfo.germanAMLChangesEnabled &&
            this.depositService?.depositInfo?.productPreference === '' &&
            !hideProductRadioButtons
        ) {
            this.behaviorData.changeProductSelectionError(true);
            return;
        }

        if (this.depositCurfewCtaDisabled) return false;

        if (threeds2Enabled) {
            this.wpSessionService.fetchWPSessionId(
                this.userDataService?.userData?.configParams?.baseWorldPayURLForDDC,
                this.userDataService?.userData?.configParams?.worldPayTokenForDDC,
                this.userDataService?.userData?.configParams?.worldPayURLForDDC,
                this.selectedOption?.startDigits,
            );
        }

        this.subscriptions$.add(
            this.behaviorData?.getProductPreference.subscribe((data) => {
                this.productPreference = data;
            }),
        );

        const bettingEnabled =
            ['pp', 'gb', 'on', 'fr', 'pj', 'bj', 'op', 'oy'].includes(this.dataService?.sessionInfo.userId.substring(0, 2)) ||
            this.dataService?.sessionInfo?.brandId.toLowerCase().includes('mgm');
        this.depositService.depositInfo.productPreference =
            !bettingEnabled && this.productPreference === ProductType.BETTING ? ProductType.SPORTSBOOK : this.productPreference;
        this.geoComplyInitialize();
        if (this.selectedOption?.paymentMethodInfo?.paymentMethod == 'INTERCHECKS_ACH') {
            const { paymentAccountId: accountId, accountHash, routingNumber } = this.selectedOption.paymentAccountInfo;
            this.depositService.depositInfo.paymentAccountInfo.cardHash = accountHash;
            this.depositService.depositInfo.paymentAccountInfo.routingNumber = routingNumber;
            this.depositService.depositInfo.paymentAccountInfo.accountId = accountId;
        }
        if (this.userDataService?.userData?.configParams?.br_ftd_consent_auth_enabled == 'Y') {
            if (this.depositService.depositInfo.paymentAccountInfo?.instrumentId !== undefined) {
                delete this.depositService.depositInfo.paymentAccountInfo.instrumentId;
            }
            this.depositService.depositInfo.details.is_consent_accepted_by_user = this.depositForm.controls?.instrumentFormGroup?.get(
                'consentForRegistration',
            )?.value
                ? 'Y'
                : 'N';
        }
        if (!threeds2Enabled || proceedToSubmit) {
            if (this.optionListService?.selectedOption?.paymentMethodInfo?.isNonPCIOption === 'true') {
                this.submitCCDeposit();
            } else {
                this.depositService.submitDeposit();
            }
        } else {
            this.depositService.depositInfo.worldPaySessionId = this.dataService?.worldPaySessionId;
            this.submitCCDeposit();
        }

        this.raiseGTMEvent();
    }
    private handleIframeResponse(data: any) {
        if (!data?.errorcode) {
            this.depositService.submitDeposit();
        }
    }
    private submitCCDeposit() {
        // NGPG CVV Submit Call

        if (this.userDataService?.userData?.configParams?.qd_ngpg_iframe_enabled === 'Y') {
            const request: CardSubmitRequest = {
                submitUrl: this.optionListService?.selectedOption?.paymentAccountInfo?.paymentAccountUpdateUrl,
                jwtToken: this.optionListService?.selectedOption?.paymentAccountInfo?.cardToken ?? '',
                usecase: CardSubmitUseCase.update_cvv,
            };
            this.cardIframeService._eventBridge()?.off(EventType.card_submit_response);
            this.cardIframeService._eventBridge()?.sendMessageToIframe({ eventName: EventType.card_submit, payload: request });
            this.cardIframeService._eventBridge()?.receiveEvent(EventType.card_submit_response, this.handleIframeResponse.bind(this));
        } else {
            this.ccDepositService.submitCCDeposit(
                this.depositService?.depositInfo?.paymentAccountInfo?.cvv2,
                this.optionListService?.selectedOption?.paymentAccountInfo?.paymentAccountUpdateUrl,
                this.optionListService?.selectedOption?.paymentAccountInfo?.pgwEnabled,
                this.depositService.handleResponse,
                this.optionListService?.selectedOption?.paymentAccountInfo?.cardToken,
            );
        }
    }
    geoComplyInitialize(): void {
        if (this.userDataService?.userData?.configParams?.GEOCOMPLY_LABEL_NAME) {
            this.geoComply.getGeoComplyInitializeInput(this.userDataService?.userData);
        }
    }

    handleSubmitResponse() {
        let depositSubmitResponse: any;
        this.subscriptions$.add(
            this.behaviorData?.getSubmitResponse.subscribe((data) => {
                if (data) {
                    depositSubmitResponse = data;
                    const { errorCode, errorMessage, allowedAmt } = depositSubmitResponse?.errorResponse || {};

                    if (depositSubmitResponse && depositSubmitResponse?.errorResponse) {
                        let amountToBePrefilled;
                        if (errorCode === ErrorCode.svocAffordibility182ErrorCodes && errorMessage == 'GROUPBASED_MONTHLY_LIMIT_TO_BE_EXCEEDED') {
                            amountToBePrefilled = allowedAmt?.value;
                        } else if (errorCode === ErrorCode.sportsLimitsErrorCode) {
                            amountToBePrefilled = depositSubmitResponse?.errorResponse?.revisedNativeAmount.toString();
                        } else if (errorCode === ErrorCode.ServiceUnavailable && errorMessage === 'KYC_LIMIT_TO_BE_EXCEEDED') {
                            const kycRemainingLimit = depositSubmitResponse?.kycCheckResponse?.kycRemainingLimit?.value;
                            amountToBePrefilled = kycRemainingLimit - this.getFeeAmount(kycRemainingLimit);
                        } else if (
                            (['177', '178', '183'].includes(errorCode) &&
                                ['AFFORDABILITY_LIMIT_GROSS_TO_BE_EXCEEDED', 'GROUPBASED_MONTHLY_LIMIT_GROSS_TO_BE_EXCEEDED'].includes(
                                    errorMessage,
                                )) ||
                            (errorCode == '179' && errorMessage == 'MILESTONE_SGQ_LIMIT_GROSS_TO_BE_EXCEEDED') ||
                            (errorCode == '180' && errorMessage == 'MILESTONE_SELF_CERT_LIMIT_GROSS_TO_BE_EXCEEDED') ||
                            (errorCode == '181' &&
                                ['DEPOSIT_12MONTHS_LIMIT_GROSS_TO_BE_EXCEEDED_BEA', 'DEPOSIT_12MONTHS_LIMIT_GROSS_TO_BE_EXCEEDED'].includes(
                                    errorMessage?.toUpperCase(),
                                )) ||
                            (errorCode == '184' && errorMessage == 'MAX_LOSS_LIMIT_MONTHLY_TO_BE_EXCEEDED')
                        ) {
                            amountToBePrefilled = allowedAmt?.value - this.getFeeAmount(allowedAmt?.value);
                            if ((amountToBePrefilled * 100) % 100 == 0) {
                                amountToBePrefilled = (amountToBePrefilled * 100) / 100;
                                this.prefillInDecimal = false;
                            } else this.prefillInDecimal = true;
                        } else if (
                            (errorCode === ErrorCode.instrumentDepositLimitExceeded || errorCode === '794') &&
                            errorMessage === 'INSTRUMENT_DEPOSIT_LIMIT_TO_BE_EXCEEDED' &&
                            depositSubmitResponse?.errorResponse?.revisedAmount
                        ) {
                            const instrumentDepositLimit = depositSubmitResponse?.errorResponse?.revisedAmount?.value;
                            amountToBePrefilled = instrumentDepositLimit - this.getFeeAmount(instrumentDepositLimit);
                        } else if (
                            (errorCode === ErrorCode.prorgDepLimitErrorCode[0] &&
                                (errorMessage?.toLowerCase() === 'deposit_12months_limit_to_be_exceeded' ||
                                    errorMessage?.toLowerCase() === 'deposit_12months_limit_to_be_exceeded_bea')) ||
                            (errorCode == '189' && errorMessage?.toLowerCase() == 'hardstop_o80_limit_to_be_exceeded') ||
                            (errorCode == '190' && errorMessage?.toLowerCase() == 'hardstop_o80_gross_limit_to_be_exceeded') ||
                            (errorCode == '191' && errorMessage.toUpperCase() == 'AFF_L5/6_LIMIT_U25_TO_BE_EXCEEDED') ||
                            (errorCode == '192' && errorMessage?.toUpperCase() == 'AFF_L5/6_LIMIT_A25_TO_BE_EXCEEDED') ||
                            (['166', '167'].includes(errorCode) && errorMessage?.toUpperCase() == 'AFFORDABILITY_LIMIT_TO_BE_EXCEEDED') ||
                            (errorCode == '193' && errorMessage?.toUpperCase() == 'AFF_L5/6_GROSSLIMIT_U25_TO_BE_EXCEEDED') ||
                            (['195', '196', '197'].includes(errorCode) &&
                                ['FRA_LIMIT_UNDER25_TO_BE_EXCEEDED', 'FRA_LIMIT_OVER25_TO_BE_EXCEEDED', 'FRA_LIMIT_OVER80_TO_BE_EXCEEDED'].includes(
                                    errorMessage?.toUpperCase(),
                                )) ||
                            (errorCode == '194' && errorMessage?.toUpperCase() == 'AFF_L5/6_GROSSLIMIT_A25_TO_BE_EXCEEDED') ||
                            (['160', '161'].includes(errorCode) && errorMessage?.toUpperCase() == 'AML_LIMIT_TO_BE_EXCEEDED')
                        ) {
                            amountToBePrefilled = allowedAmt?.value - this.getFeeAmount(allowedAmt?.value);
                            this.prefillInDecimal = true;
                        } else if (
                            (errorCode === ErrorCode.prorgDepLimitErrorCode[1] &&
                                errorMessage?.toLowerCase() === 'deposit_30days_limit_to_be_exceeded') ||
                            errorMessage?.toLowerCase() === 'aml_limit_to_be_exceeded'
                        ) {
                            amountToBePrefilled = allowedAmt?.value - this.getFeeAmount(allowedAmt?.value);
                            this.prefillInDecimal = true;
                        } else if (
                            ErrorCode.affordibilityDepLimitErrorCodes.includes(errorCode) &&
                            [
                                'MILESTONE_SELF_CERT_LIMIT_TO_BE_EXCEEDED',
                                'MILESTONE_SGQ_LIMIT_TO_BE_EXCEEDED',
                                'TU_DEPOSIT_LIMIT_TO_BE_EXCEEDED',
                                'HARDSTOP_MONTHLY_LIMIT_TO_BE_EXCEEDED',
                                'AML_LIMIT_TO_BE_EXCEEDED',
                            ].includes(errorMessage?.toUpperCase())
                        ) {
                            amountToBePrefilled = allowedAmt?.value - this.getFeeAmount(allowedAmt?.value);
                            this.prefillInDecimal = true;
                        } else if (
                            (errorCode === ErrorCode.centralDepLimitErrorCodes[0] || errorMessage === ErrorCode.centralDepLimitErrorCodes[1]) &&
                            errorMessage === 'DEPOSIT_EXCEEDS_BUDGET'
                        ) {
                            if (parseFloat(depositSubmitResponse?.errorResponse?.remainingAmount) > parseFloat('0')) {
                                amountToBePrefilled = depositSubmitResponse?.errorResponse?.remainingAmount;
                                this.prefillInDecimal = true;
                            } else {
                                amountToBePrefilled = '0';
                            }
                        } else if (
                            errorCode === ErrorCode.maxFailDepEnhancementErrorCode &&
                            depositSubmitResponse?.errorResponse?.templateName == ErrorTemplate.maxFailDepEnhancementErrorTemplate &&
                            depositSubmitResponse?.errorResponse?.additionalData?.userMaxFailDepStatus
                        ) {
                            this.behaviorData.changeMaxFailDepEnhancement.next(
                                depositSubmitResponse?.errorResponse?.additionalData?.userMaxFailDepStatus,
                            );
                        } else if (ErrorCode.SvocPurchaseLimits.includes(errorCode) && allowedAmt?.value > 0) {
                            amountToBePrefilled = allowedAmt?.value - this.getFeeAmount(allowedAmt?.value);
                            this.prefillInDecimal = true;
                        }
                        if (
                            (depositSubmitResponse?.kycCheckResponse && Jurisdiction.DEU === depositSubmitResponse?.kycCheckResponse?.jurisdiction) ||
                            this.prefillInDecimal
                        ) {
                            amountToBePrefilled = parseFloat(amountToBePrefilled).toFixed(2);
                        } else {
                            amountToBePrefilled = Math.floor(parseFloat(amountToBePrefilled));
                        }
                        if (depositSubmitResponse?.kycCheckResponse && depositSubmitResponse?.kycCheckResponse?.productPreference) {
                            this.depositService.depositInfo.productPreference = depositSubmitResponse?.kycCheckResponse?.productPreference;
                        }
                        if (isNaN(+amountToBePrefilled) && depositSubmitResponse?.amount) {
                            amountToBePrefilled = depositSubmitResponse?.amount?.value;
                        }

                        this.prefillNavAmount(amountToBePrefilled);
                    }

                    if (!this.depositService?.depositInfo?.amountInAccountCurrency) {
                        this.depositService.depositInfo.amountInAccountCurrency = '';
                    }
                    // const paymentMethod = this.selectedOption?.paymentMethodInfo?.paymentMethod;
                    // this.gtmService.raiseEvent('quickDepositLoad', paymentMethod?.toLowerCase() + 'Input', paymentMethod);
                }
            }),
        );
    }

    getFeeAmount(amount: any) {
        return this.depositService.getFeeAmount(amount);
    }

    raiseGTMEvent() {
        const eventData = structuredClone(this.gtmService.gtmEventData);
        eventData.component.CategoryEvent = 'cashier';
        eventData.component.LabelEvent = 'deposit process';
        eventData.component.ActionEvent = 'click';
        eventData.component.LocationEvent = 'quick deposit page';
        eventData.component.EventDetails = 'deposit';
        eventData.component.PositionEvent = 'deposit submit';
        eventData.component.URLClicked = 'not applicable';
        eventData.payment.method = this.selectedOption?.paymentMethodInfo?.paymentMethod?.toLowerCase();
        this.gtmService.raiseComponentEvent('Event.Tracking', eventData);
    }

    getBehaviorData() {
        this.subscriptions$.add(
            this.behaviorData?.getKycPLverification.subscribe((data) => {
                this.kycPLverification = data;
            }),
        );

        this.subscriptions$.add(
            this.behaviorData?.getPaypalBadFunding.subscribe((data) => {
                this.isPaypalBadFunding = data;
            }),
        );
    }

    getActiveDepositCurfew(data: boolean) {
        this.depositCurfewCtaDisabled = data;
    }

    ngAfterViewChecked() {
        this.changeDetector.detectChanges();
    }

    get getIsSubmitButtonDisabled() {
        return (
            this.optionListService?.selectedOption?.paymentAccountInfo?.isESRiskPlayer == 'Y' ||
            (this.optionListService?.selectedOption?.paymentAccountInfo?.ESIntensiveGreyOut == 'true' &&
                this.optionListService?.selectedOption?.creditCard) ||
            !this.depositForm.valid ||
            this.depositCurfewCtaDisabled ||
            this.loaderService.depositRedirectInProgress() ||
            (this.isPaypalBadFunding && this.depositService.depositInfo?.paymentMethod == 'PAYPALDIRECT')
        );
    }

    resetDepositData() {
        this.depositService.showDeclinePage.set(false);
        this.depositService.showDepositFailure.set(false);
        this.depositService.showDepositSuccess.set(false);
        this.dataService.depositProcess.set(false);
        this.behaviorData.changeSubmitResponse(null);
        this.loaderService.depositRedirectInProgress.set(false);
    }
    ngOnDestroy() {
        this.resetDepositData();
        this.subscriptions$?.unsubscribe();
    }
}
